import { useEffect, useState } from "react";
import { Ticket, Star, Copy, Check, TrendingUp, Send, Key, Plus, X } from "lucide-react";
import { myApi } from "../api";
import { verifyEmail } from "@/shared/emailValidation";

function Tab({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition ${
        active ? "bg-[#0F1829] text-white" : "bg-white border border-[#E6E1D6] text-[#4B5563] hover:border-[#978D78]"
      }`}
    >
      <Icon size={15} strokeWidth={1.7} /> {label}
      {count !== undefined && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] ${active ? "bg-white/20" : "bg-[#F5F2EB]"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

export default function MyWallet() {
  const [tab, setTab] = useState("vales");
  return (
    <div data-testid="my-wallet-page" className="space-y-6">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Vales & Prémios
        </h1>
        <p className="text-sm text-[#6B7280]">A sua carteira de vales IHTP e progresso nos prémios.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Tab active={tab === "vales"} onClick={() => setTab("vales")} icon={Ticket} label="Vales" testid="my-wallet-tab-vales" />
        <Tab active={tab === "premios"} onClick={() => setTab("premios")} icon={Star} label="Prémios" testid="my-wallet-tab-premios" />
      </div>

      {tab === "vales" && <Vales />}
      {tab === "premios" && <Premios />}
    </div>
  );
}

// ─── Vales ──────────────────────────────────────────────
function Vales() {
  const [resumo, setResumo] = useState(null);
  const [status, setStatus] = useState("disponivel");
  const [list, setList] = useState(null);
  const [copied, setCopied] = useState(null);
  const [openModal, setOpenModal] = useState(null); // 'activate-mine' | 'activate-guid' | 'send'
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'err', message}

  const reloadAll = () => {
    myApi.valesResumo().then(setResumo).catch(() => setResumo(null));
    setList(null);
    myApi.vales(status).then(setList).catch(() => setList([]));
  };

  useEffect(() => { myApi.valesResumo().then(setResumo).catch(() => setResumo(null)); }, []);
  useEffect(() => {
    setList(null);
    myApi.vales(status).then(setList).catch(() => setList([]));
  }, [status]);

  // Auto-detect ?guid=xxx no URL para abrir directamente o modal de activação
  // (link clicado no email enviado por outro cliente).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incoming = params.get("guid");
    if (incoming) setOpenModal({ type: "activate-guid", initialValue: incoming });
  }, []);

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code); setTimeout(() => setCopied(null), 1500);
  };

  return (
    <>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#0F1829] to-[#1a2542] text-white rounded-[20px] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Valor Disponível</div>
          <div className="text-4xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {resumo ? fmt(resumo.valor_disponivel) : "—"}
          </div>
          <div className="text-xs text-white/60 mt-2">{resumo?.num_disponiveis ?? 0} vales disponíveis</div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-2">Disponíveis</div>
          <div className="text-4xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {resumo?.num_disponiveis ?? 0}
          </div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-2">Utilizados / Atribuídos</div>
          <div className="text-4xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {resumo?.num_utilizados ?? 0}
          </div>
        </div>
      </div>

      {/* Action panel — apenas "activar recebido" (input de GUID externo).
          As acções "activar meu" e "enviar" são por-vale e estão inline na
          lista de vales abaixo (botões na coluna "Acções"). */}
      <div className="grid sm:grid-cols-1 gap-3 mt-6">
        <button type="button" onClick={() => setOpenModal({ type: "activate-guid" })}
          data-testid="vale-action-activate-guid"
          className="flex items-center gap-3 p-4 bg-white border border-[#E6E1D6] rounded-[16px] hover:border-[#0F1829] hover:bg-[#F5F2EB] transition group text-left">
          <div className="w-10 h-10 rounded-xl bg-[#0F1829]/5 flex items-center justify-center text-[#0F1829] group-hover:bg-[#0F1829] group-hover:text-white transition"><Key size={18} strokeWidth={2} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0F1829]">Activar vale recebido</div>
            <div className="text-xs text-[#6B7280]">Insira o código GUID que recebeu por email</div>
          </div>
        </button>
      </div>

      {feedback && (
        <div data-testid="vale-feedback" className={`mt-4 px-4 py-3 rounded-xl text-sm ${feedback.type === "ok" ? "bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]" : "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]"}`}>
          {feedback.message}
        </div>
      )}

      <div className="flex gap-2 mt-6">
        <button onClick={() => setStatus("disponivel")} data-testid="vales-filter-disp" className={`px-3 py-1.5 text-xs rounded-full uppercase tracking-[0.15em] ${status === "disponivel" ? "bg-[#0F1829] text-white" : "bg-white border border-[#E6E1D6] text-[#4B5563]"}`}>
          Disponíveis
        </button>
        <button onClick={() => setStatus("utilizado")} data-testid="vales-filter-util" className={`px-3 py-1.5 text-xs rounded-full uppercase tracking-[0.15em] ${status === "utilizado" ? "bg-[#0F1829] text-white" : "bg-white border border-[#E6E1D6] text-[#4B5563]"}`}>
          Utilizados
        </button>
        <button onClick={() => setStatus("todos")} data-testid="vales-filter-all" className={`px-3 py-1.5 text-xs rounded-full uppercase tracking-[0.15em] ${status === "todos" ? "bg-[#0F1829] text-white" : "bg-white border border-[#E6E1D6] text-[#4B5563]"}`}>
          Todos
        </button>
      </div>

      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden mt-4">
        {list === null ? (
          <div className="p-10 text-center text-sm text-[#978D78]">A carregar...</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#4B5563]">
            {(() => {
              let suffix = "";
              if (status === "disponivel") suffix = "disponíveis";
              else if (status === "utilizado") suffix = "utilizados";
              return `Sem vales ${suffix}.`;
            })()}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#F5F2EB]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Código</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Valor</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Remanescente</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Estado</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Acções</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F2EB]">
              {list.map((v) => {
                const consumed = Number(v.Valor || 0) - Number(v.ValorRemanescente || 0);
                const remanescente = Number(v.ValorRemanescente || 0);
                const fullyUsed = remanescente <= 0;
                const partiallyUsed = !fullyUsed && consumed > 0.001;
                const receivedFromOther = v.ID_Cliente !== undefined && v.ID_Cliente_Activacao !== undefined
                  && v.ID_Cliente_Activacao && v.ID_Cliente_Activacao !== v.ID_Cliente;
                const isMine = !receivedFromOther;
                const notActivated = !v.ID_Cliente_Activacao;
                // "Activar" visível em qualquer vale com saldo > 0 (próprio ou recebido).
                // "Enviar" só em vales próprios ainda não activados (recebidos não podem ser encaminhados).
                const canActivate = remanescente > 0;
                const canSend = isMine && notActivated && remanescente > 0;
                let stateLabel, stateClass;
                if (fullyUsed) {
                  stateLabel = v.UtilizadoPor ? `Usado por ${v.UtilizadoPor}` : "Utilizado";
                  stateClass = "bg-[#F5F2EB] text-[#978D78]";
                } else if (notActivated && isMine) {
                  stateLabel = "Por activar";
                  stateClass = "bg-amber-50 text-amber-700";
                } else if (partiallyUsed) {
                  stateLabel = `Parcial — restam ${fmt(v.ValorRemanescente)}`;
                  stateClass = "bg-amber-50 text-amber-700";
                } else {
                  stateLabel = "Disponível";
                  stateClass = "bg-[#6F7559]/15 text-[#4A5E2E]";
                }
                return (
                  <tr key={v.ID_Vale} data-testid={`my-vale-${v.ID_Vale}`}>
                    <td className="px-5 py-3">
                      <button onClick={() => copy(v.Codigo)} className="inline-flex items-center gap-2 text-xs font-mono text-[#0F1829] hover:text-[#978D78]">
                        {v.Codigo}
                        {copied === v.Codigo ? <Check size={13} className="text-[#6F7559]" /> : <Copy size={13} />}
                      </button>
                      {receivedFromOther && (
                        <div className="text-[10px] text-[#978D78] mt-0.5 uppercase tracking-wider">
                          Recebido
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                      {fmt(v.Valor)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-[#4B5563]">
                      {fmt(v.ValorRemanescente)}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider ${stateClass}`}>
                        {stateLabel}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {canActivate ? (
                          <button
                            type="button"
                            onClick={() => {
                              // Próprio + ainda por activar → abre modal de activação.
                              // Já activado (próprio ou recebido) → feedback informativo.
                              if (isMine && notActivated) {
                                setOpenModal({ type: "activate-mine", initialValue: String(v.ID_Vale) });
                              } else {
                                setFeedback({ type: "ok", message: "Este vale já está activo. Pode usá-lo no próximo checkout." });
                                setTimeout(() => setFeedback(null), 4000);
                              }
                            }}
                            data-testid={`vale-activate-${v.ID_Vale}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:text-[#4A5E2E] underline underline-offset-2"
                          >
                            <Plus size={12} strokeWidth={2.4} /> Activar
                          </button>
                        ) : null}
                        {canSend ? (
                          <button
                            type="button"
                            onClick={() => setOpenModal({ type: "send", initialValue: String(v.ID_Vale) })}
                            data-testid={`vale-send-${v.ID_Vale}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#002480] hover:text-[#0F1829] underline underline-offset-2"
                          >
                            <Send size={12} strokeWidth={2.4} /> Enviar
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ValesUtilizacoes />

      {openModal && (
        <ValeModal
          modal={openModal}
          onClose={() => setOpenModal(null)}
          onSuccess={(message) => {
            setFeedback({ type: "ok", message });
            setOpenModal(null);
            reloadAll();
            setTimeout(() => setFeedback(null), 5000);
          }}
          onError={(message) => {
            setFeedback({ type: "err", message });
            setTimeout(() => setFeedback(null), 6000);
          }}
        />
      )}
    </>
  );
}

function ValeModal({ modal, onClose, onSuccess, onError }) {
  const isMine = modal.type === "activate-mine";
  const isGuid = modal.type === "activate-guid";
  const isSend = modal.type === "send";
  const [codigo, setCodigo] = useState(modal.initialValue || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] transition-all";

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (isSend) {
      const ev = await verifyEmail(email);
      if (!ev.valid) {
        onError(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
        return;
      }
    }
    setBusy(true);
    try {
      let r;
      if (isMine) r = await myApi.valesActivate(codigo);
      else if (isGuid) r = await myApi.valesActivateByGuid(codigo);
      else r = await myApi.valesSend(codigo, email);
      if (r?.success) onSuccess(r?.message || "Operação concluída.");
      else onError(r?.message || "Não foi possível processar o pedido.");
    } catch (err) {
      onError(err?.message || "Erro inesperado.");
    } finally {
      setBusy(false);
    }
  };

  let title, label, placeholder, helper;
  if (isMine) {
    title = "Activar vale meu";
    label = "Código de Vale (ID)";
    placeholder = "Ex: 12345";
    helper = "Insira o ID numérico do vale para o tornar disponível para uso.";
  } else if (isGuid) {
    title = "Activar vale recebido";
    label = "Código GUID do Vale";
    placeholder = "Ex: 7B5B7BC1-...";
    helper = "Insira o GUID que recebeu por email para activar o vale na sua conta.";
  } else {
    title = "Enviar vale por email";
    label = "Código de Vale (ID)";
    placeholder = "Ex: 12345";
    helper = "O destinatário receberá um email com instruções para activar.";
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid={`vale-modal-${modal.type}`}>
      <div className="bg-white rounded-[20px] max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>{title}</h2>
          <button type="button" onClick={onClose} className="text-[#978D78] hover:text-[#0F1829]" data-testid="vale-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-[#6B7280] mb-1 block">{label}</label>
            <input
              type="text" className={inputCls} placeholder={placeholder}
              value={codigo} onChange={(e) => setCodigo(e.target.value.trim())}
              required autoFocus
              data-testid="vale-modal-codigo-input"
            />
          </div>
          {isSend && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.15em] text-[#6B7280] mb-1 block">Email do destinatário</label>
              <input
                type="email" className={inputCls} placeholder="exemplo@email.com"
                value={email} onChange={(e) => setEmail(e.target.value.trim())}
                required
                data-testid="vale-modal-email-input"
              />
            </div>
          )}
          <p className="text-xs text-[#6B7280]">{helper}</p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="flex-1 px-4 py-2.5 bg-white border border-[#E5E7EB] text-[#4B5563] rounded-full text-sm hover:bg-[#F5F2EB] transition disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={busy || !codigo || (isSend && !email)}
              data-testid="vale-modal-submit"
              className="flex-1 px-4 py-2.5 bg-[#0F1829] text-white rounded-full text-sm hover:bg-[#1a2542] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "A processar..." : (isSend ? "Enviar" : "Activar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ValesUtilizacoes() {
  const [list, setList] = useState(null);
  useEffect(() => { myApi.valesUtilizacoes().then(setList).catch(() => setList([])); }, []);
  if (list === null) return null;
  if (list.length === 0) return null;
  const total = list.reduce((s, e) => s + Number(e.DescontoVale || 0), 0);
  return (
    <div className="mt-8" data-testid="my-vale-utilizacoes">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-lg text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Encomendas com vales utilizados
        </h3>
        <span className="text-xs text-[#978D78]">
          Total descontado: <span className="text-[#0F1829] font-medium">{fmt(total)}</span>
        </span>
      </div>
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F5F2EB]">
            <tr>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Encomenda</th>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Data</th>
              <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Total</th>
              <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Desconto Vale</th>
              <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F5F2EB]">
            {list.map((e) => (
              <tr key={e.ID_Encomenda} data-testid={`my-vale-uso-${e.ID_Encomenda}`}>
                <td className="px-5 py-3 text-sm text-[#0F1829] font-mono">#{e.ID_Encomenda}</td>
                <td className="px-5 py-3 text-sm text-[#4B5563]">{(e.Data || "").slice(0, 10).split("-").reverse().join("/")}</td>
                <td className="px-5 py-3 text-right text-sm text-[#4B5563]">{fmt(e.PrecoFinal)}</td>
                <td className="px-5 py-3 text-right text-sm text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                  −{fmt(e.DescontoVale)}
                </td>
                <td className="px-5 py-3">
                  <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#F5F2EB] text-[#978D78]">
                    {e.ID_EstadoEncomenda}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Prémios ─────────────────────────────────────────────
function Premios() {
  const [data, setData] = useState(null);

  useEffect(() => { myApi.premios().then(setData).catch(() => setData({ situacao: {}, acumulado: 0, niveis: [] })); }, []);

  if (!data) return <div className="text-sm text-[#978D78]">A carregar...</div>;

  const { situacao, acumulado, niveis } = data;

  return (
    <>
      <div className="bg-gradient-to-br from-[#6F4F1D] to-[#978D78] text-white rounded-[24px] p-8" data-testid="my-premios-hero">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Situação Actual</div>
        <div className="flex flex-wrap items-end gap-8">
          <div>
            <div className="text-5xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
              {fmt(acumulado)}
            </div>
            <div className="text-xs text-white/60 mt-1">Consumo pessoal ano {new Date().getFullYear()}</div>
          </div>
          {situacao?.Stars > 0 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: situacao.Stars || 0 }).map((_, i) => (
                <Star key={`star-${i}`} size={22} className="fill-[#D1CFC9] text-[#D1CFC9]" />
              ))}
            </div>
          )}
          {situacao?.ValorAteProximoPremio > 0 && (
            <div className="ml-auto">
              <div className="text-xs text-white/60 uppercase tracking-[0.2em] mb-1">Falta para o próximo</div>
              <div className="text-2xl" style={{ fontFamily: "'Fraunces', serif" }}>
                {fmt(situacao.ValorAteProximoPremio)}
              </div>
            </div>
          )}
        </div>
        {/* breakdown */}
        {(situacao?.VolumePessoal != null || situacao?.VolumeClientes != null || situacao?.VolumeTalentos != null) && (
          <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-white/10" data-testid="my-premios-breakdown">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-0.5">Pessoal</div>
              <div className="text-lg" style={{ fontFamily: "'Fraunces', serif" }}>
                {fmt(situacao.VolumePessoal || 0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-0.5">Clientes</div>
              <div className="text-lg" style={{ fontFamily: "'Fraunces', serif" }}>
                {fmt(situacao.VolumeClientes || 0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-0.5">Talentos</div>
              <div className="text-lg" style={{ fontFamily: "'Fraunces', serif" }}>
                {fmt(situacao.VolumeTalentos || 0)}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-xl text-[#0F1829] mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Níveis de Prémio IHTP
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {niveis.map((n) => (
            <div
              key={n.ID_Premio}
              data-testid={`my-premio-${n.ID_Premio}`}
              className={`rounded-[20px] p-5 border transition ${
                n.Atingido ? "bg-[#0F1829] text-white border-[#0F1829]" : "bg-white border-[#E6E1D6]"
              }`}
            >
              <div className="flex items-start gap-3">
                <Star size={22} className={n.Atingido ? "text-[#D1CFC9] fill-[#D1CFC9]" : "text-[#978D78]"} />
                <div className="flex-1">
                  <div className={`text-sm leading-snug ${n.Atingido ? "text-white" : "text-[#0F1829]"}`}>
                    {n.Descricao}
                  </div>
                  <div className={`text-xs mt-2 ${n.Atingido ? "text-[#D1CFC9]" : "text-[#978D78]"}`}>
                    Meta: {fmt(n.Valor)}
                  </div>
                  <div className="mt-3">
                    <div className={`h-2 rounded-full ${n.Atingido ? "bg-white/20" : "bg-[#F5F2EB]"}`}>
                      <div
                        className={`h-full rounded-full ${n.Atingido ? "bg-[#D1CFC9]" : "bg-gradient-to-r from-[#978D78] to-[#0F1829]"}`}
                        style={{ width: `${Math.round((n.Progresso || 0) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className={n.Atingido ? "text-[#D1CFC9]" : "text-[#4B5563]"}>
                        {Math.round((n.Progresso || 0) * 100)}%
                      </span>
                      {n.Atingido ? (
                        <span className="inline-flex items-center gap-1 text-[#D1CFC9]">
                          <Check size={13} /> Atingido
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#978D78]">
                          <TrendingUp size={13} /> Faltam {fmt(n.Falta)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {niveis.length === 0 && (
            <div className="col-span-full text-sm text-[#6B7280] bg-white rounded-[20px] p-8 text-center border border-[#E6E1D6]">
              Sem níveis definidos.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
